import {
    buildTachoRequestItem,
    getTachoVisibleItems,
    hasTachoDebriefDraftChanges,
    tachoItemsPageSize,
    toClearItemsBeforeInstant
} from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';
import { TachoInfringementsStep } from 'pages/employee-reviews/complete/steps';
import { tachoInfringementsTopicKey } from 'pages/employee-reviews/shared';

// The step unmounts whenever the wizard moves off it, so the debriefs recorded so far, the
// infringement being debriefed and the table's page all live in the page's topic state.
// records holds each debrief by the item it was recorded against and is kept when the items
// are re-read.
function createTachoInfringementsState() {
    return {
        items: [],
        records: {},
        clearItemsBefore: null,
        markAllReviewed: false,
        allowMarkAllReviewed: false,
        outstandingItemCount: 0,
        totalGfpCost: null,
        itemsUnavailableMessage: null,
        comment: "",
        pageNumber: 1,
        pageSize: tachoItemsPageSize,
        debrief: null,
        editing: false
    };
}

// Only a debrief the manager recorded against a listed item is sent - those covered by marking
// all as reviewed or by the clear-items-before date are resolved by the API from the two flags.
// A debrief recorded against an item the clear date now covers stays in state so it returns if
// the date moves, but is not sent because the item is cleared.
function getRequestFields(state) {
    const records = state?.records ?? {};
    const items = getTachoVisibleItems(state)
        .filter((item) => Object.hasOwn(records, item.itemReference))
        .map((item) => buildTachoRequestItem(item.itemReference, records[item.itemReference]));

    return {
        items,
        clearItemsBefore: toClearItemsBeforeInstant(state?.clearItemsBefore),
        markAllReviewed: state?.markAllReviewed === true
    };
}

// The Tacho Infringements topic. The driver's outstanding infringements arrive with the review
// itself and every debrief is sent with the completion request, so nothing is read or written
// through an endpoint of the topic's own.
export const tachoInfringementsTopic = {
    key: tachoInfringementsTopicKey,
    stepTitle: "Tacho Infringements",
    Step: TachoInfringementsStep,
    createInitialState: createTachoInfringementsState,
    load: (employeeId, topicEntry) => Promise.resolve(topicEntry),
    loadFailureMessage: "Failed to get the tacho infringements. Please refresh the page.",
    applyLoaded: (entry) => ({
        items: entry?.items ?? [],
        allowMarkAllReviewed: entry?.allowMarkAllReviewed === true,
        outstandingItemCount: entry?.outstandingItemCount ?? 0,
        totalGfpCost: entry?.totalGfpCost ?? null,
        itemsUnavailableMessage: entry?.itemsUnavailableMessage ?? null
    }),
    getGating: (state) => {
        const canProceed = state?.editing !== true;

        return {
            canProceed,
            cantProceedTooltip: canProceed ? null : "Save or cancel the infringement you are debriefing before continuing."
        };
    },
    isDirty: (state) => Object.keys(state?.records ?? {}).length > 0
        || state?.clearItemsBefore != null
        || state?.markAllReviewed === true
        || (state?.comment ?? "") !== ""
        || hasTachoDebriefDraftChanges(state),
    getComment: (state) => state.comment,
    getRequestFields
};
